import { Navigate, useParams } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  MoneyDisplay,
  Spinner,
} from '@shared/ui';
import { ROUTES } from '@shared/config/routes';
import { useOrderQuery } from '@entities/order';
import { UserCell } from '@entities/user-directory';
import { OrderStatusChip } from '@features/order-list';
import {
  OrderDocuments,
  OrderHistory,
  StatusActions,
} from '@features/order-detail';
import { ShipmentSection } from '@features/shipment';

export function OrderDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { data: order, isLoading, isError } = useOrderQuery(id);

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner />
      </div>
    );
  }
  if (isError || !order) return <Navigate to={ROUTES.orders} replace />;

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex flex-col gap-1">
          <h1 className="text-2xl font-semibold">{order.title}</h1>
          <p className="font-mono text-xs text-muted-foreground">{order.id}</p>
          <div className="mt-1">
            <UserCell userId={order.buyerId} user={order.buyer} size="sm" showRole />
          </div>
        </div>
        <div className="flex flex-col items-end gap-1">
          <OrderStatusChip status={order.status} />
          <p className="text-sm">
            <MoneyDisplay money={order.totalAmount} />
          </p>
          {order.leadTimeDays != null ? (
            <p className="text-xs text-muted-foreground">{order.leadTimeDays} days lead time</p>
          ) : null}
        </div>
      </header>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="flex flex-col gap-6 lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Status</CardTitle>
            </CardHeader>
            <CardContent>
              <StatusActions order={order} />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Documents</CardTitle>
            </CardHeader>
            <CardContent>
              <OrderDocuments order={order} />
            </CardContent>
          </Card>

          <ShipmentSection
            orderId={order.id}
            canAssign={
              order.status === 'IN_PROCESSING' ||
              order.status === 'READY_TO_SHIP' ||
              order.status === 'IN_TRANSIT'
            }
          />
        </div>

        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle className="text-base">History</CardTitle>
          </CardHeader>
          <CardContent>
            <OrderHistory order={order} />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
